/**
 * 신규 점포 개설 절차 페이지 v2.
 *
 * 구조:
 * 1. 마스터 스위치 — "필요한 절차만 보기" (ON = dim 카드 완전 숨김)
 * 2. 토글 패널 — 매장 조건 15개 설정 (펼침/접힘)
 * 3. 필수 단계 9개 — P-2·P-3·P-1·(A·B·C 병렬)·D·E·F
 * 4. 조건부 추가 인허가 7종 — 점선 테두리 + vertical list
 *
 * 권위 문서: docs/console/2026-05-14_corporate-store-opening-procedure.md
 * 비즈니스 로직: stepActivation.ts isStepActive()
 * 상태: useState만 (XState 불필요 — 정적 토글)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { CORE_STEPS_LIST, ADDON_STEPS_LIST } from './steps';
import type { StepId } from './steps';
import { DEFAULT_TOGGLES } from './toggleConfig';
import type { ToggleState } from './toggleConfig';
import { isStepActive } from './stepActivation';
import { StepCard } from './StepCard';
import { ParallelRow } from './ParallelRow';
import { VerticalArrow, BranchArrow, MergeArrow } from './FlowArrow';
import { ToggleControlsPanel } from './ToggleControlsPanel';
import { StepDetailDialog } from './StepDetailDialog';

const STORAGE_KEY = 'store-opening-toggles-v1';
const FILTER_STORAGE_KEY = 'store-opening-filter-v1';

// 핵심 단계 빠른 접근
const coreMap = Object.fromEntries(CORE_STEPS_LIST.map((s) => [s.id, s]));
const allStepsMap = Object.fromEntries(
  [...CORE_STEPS_LIST, ...ADDON_STEPS_LIST].map((s) => [s.id, s])
);

export function StoreOpeningPage() {
  const nav = useNavigate();

  // 변경 1: localStorage 우선, 없으면 default
  const [toggles, setToggles] = useState<ToggleState>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT_TOGGLES, ...JSON.parse(raw) };
    } catch {}
    return DEFAULT_TOGGLES;
  });

  const [filterMode, setFilterMode] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(FILTER_STORAGE_KEY);
      if (raw !== null) return JSON.parse(raw) === true;
    } catch {}
    return false;
  });

  const [selectedStepId, setSelectedStepId] = useState<StepId | null>(null);

  // debounced save — 200ms (렉 방지)
  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(toggles)); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [toggles]);

  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterMode)); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [filterMode]);

  function patchToggles(patch: Partial<ToggleState>) {
    setToggles((prev) => ({ ...prev, ...patch }));
  }

  function active(stepId: string): boolean {
    return isStepActive(stepId, toggles);
  }

  /** 마스터 스위치: ON이면 비활성 카드 display:none, OFF면 dim */
  function shouldShow(stepId: string): boolean {
    if (!filterMode) return true;
    return active(stepId);
  }

  const stepP2 = coreMap['P2']!;
  const stepP3 = coreMap['P3']!;
  const stepP1 = coreMap['P1']!;
  const stepA = coreMap['A']!;
  const stepB = coreMap['B']!;
  const stepC = coreMap['C']!;
  const stepD = coreMap['D']!;
  const stepE = coreMap['E']!;
  const stepE2 = coreMap['E2']!;
  const stepF = coreMap['F']!;

  // P-2·P-3 가맹 블록 전체가 dim인지 (화살표 dim 계산용)
  const franchiseDim = !active('P2');

  // E-2 동적 위치: 사업자단위과세(종된 사업장) + 지점 등기 + 법인 직영 → E-2가 E 선행
  const e2Before = toggles.corporateTaxMode === 'unit-tax'
    && toggles.branchRegistration
    && toggles.businessType === 'corporate';

  // 가시 여부 (필터 모드)
  const showP2 = shouldShow('P2');
  const showP3 = shouldShow('P3');
  const showB = shouldShow('B');
  const showC = shouldShow('C');
  const showE2 = shouldShow('E2');

  return (
    <div className="min-h-screen flex flex-col bg-surface-alt">
      {/* 헤더 */}
      <header className="h-14 border-b border-bdr-subtlest bg-surface flex items-center px-6 gap-3">
        <button
          onClick={() => nav('/')}
          className="flex items-center gap-1.5 text-xs text-fg-subtler hover:text-fg"
          aria-label="홈으로"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-fg flex items-center justify-center">
            <span className="text-surface text-xs font-bold">C</span>
          </div>
          <span className="font-bold text-sm">ATGRD 설정콘솔</span>
        </div>
      </header>

      <main className="flex-1 p-6 max-w-3xl mx-auto w-full space-y-6">

        {/* 페이지 헤더 */}
        <div>
          <h1 className="text-xl font-bold">신규 점포 개설 절차</h1>
          <p className="text-xs text-fg-subtler mt-1">
            토글로 우리 매장 조건을 선택하면 필요한 절차만 보입니다
          </p>
        </div>

        {/* 마스터 스위치 */}
        <div className="flex items-center justify-between rounded-xl border border-bdr-subtlest bg-surface px-5 py-4">
          <div>
            <p className="text-sm font-semibold">필요한 절차만 보기</p>
            <p className="text-[11px] text-fg-subtler mt-0.5">
              OFF: 해당 없는 단계를 흐리게 표시 &nbsp;/&nbsp; ON: 완전히 숨김
            </p>
          </div>
          <MasterSwitch checked={filterMode} onToggle={() => setFilterMode((v) => !v)} />
        </div>

        {/* 토글 패널 */}
        <ToggleControlsPanel state={toggles} onChange={patchToggles} />

        {/* ───── 필수 단계 (core 9단계) ───── */}
        <div>
          <SectionLabel>필수 절차</SectionLabel>
          <div className="flex flex-col items-center space-y-0">

            {/* P-2: 가맹 선행 */}
            {showP2 && (
              <div className="w-full max-w-sm">
                <StepCard step={stepP2} dim={!active('P2')} onClick={stepP2.detail ? () => setSelectedStepId('P2') : undefined} />
              </div>
            )}
            {showP2 && <VerticalArrow dim={franchiseDim} />}

            {/* P-3: 가맹 선행 */}
            {showP3 && (
              <div className="w-full max-w-sm">
                <StepCard step={stepP3} dim={!active('P3')} onClick={stepP3.detail ? () => setSelectedStepId('P3') : undefined} />
              </div>
            )}
            {showP3 && <VerticalArrow dim={franchiseDim} />}

            {/* P-1: 임대차계약 (항상) */}
            <div className="w-full max-w-sm">
              <StepCard step={stepP1} dim={false} onClick={stepP1.detail ? () => setSelectedStepId('P1') : undefined} />
            </div>

            {/* P-1 → A·B·C 분기 */}
            <div className="w-full">
              <BranchArrow dim={false} />
            </div>

            {/* A · B · C 병렬 */}
            <div className="w-full">
              <ParallelRow
                steps={[stepA, stepB, stepC]}
                dimIds={[
                  ...(!active('B') ? ['B'] : []),
                  ...(!active('C') ? ['C'] : []),
                ]}
                hideIds={filterMode
                  ? [
                      ...(!showB ? ['B'] : []),
                      ...(!showC ? ['C'] : []),
                    ]
                  : []}
                onClickStep={(id) => {
                  const s = allStepsMap[id];
                  if (s?.detail) setSelectedStepId(id as StepId);
                }}
              />
            </div>

            {/* A·B·C → D 합류 */}
            <div className="w-full">
              <MergeArrow dim={false} />
            </div>

            {/* D: 영업신고증 (항상) */}
            <div className="w-full max-w-sm">
              <StepCard step={stepD} dim={false} onClick={stepD.detail ? () => setSelectedStepId('D') : undefined} />
            </div>

            {/* E-2 before E: 사업자단위과세(종된 사업장) 선택 시 지점 등기가 E 선행 */}
            {e2Before && showE2 ? (
              <>
                <VerticalArrow dim={!active('E2')} />
                <div className="w-full max-w-sm">
                  <StepCard step={stepE2} dim={!active('E2')} onClick={stepE2.detail ? () => setSelectedStepId('E2') : undefined} />
                </div>
                <VerticalArrow dim={false} />
              </>
            ) : (
              <VerticalArrow dim={false} />
            )}

            {/* E: 사업자등록증 (항상) */}
            <div className="w-full max-w-sm">
              <StepCard step={stepE} dim={false} onClick={stepE.detail ? () => setSelectedStepId('E') : undefined} />
            </div>

            {/* E-2 after E: 별도 사업자등록 또는 기본 순서일 때 */}
            {!e2Before && showE2 && (
              <>
                <VerticalArrow dim={!active('E2')} />
                <div className="w-full max-w-sm">
                  <StepCard step={stepE2} dim={!active('E2')} onClick={stepE2.detail ? () => setSelectedStepId('E2') : undefined} />
                </div>
              </>
            )}

            <VerticalArrow dim={false} />

            {/* F: 통신판매업 신고 */}
            {shouldShow('F') && (
              <div className="w-full max-w-sm">
                <StepCard step={stepF} dim={!active('F')} onClick={stepF.detail ? () => setSelectedStepId('F') : undefined} />
              </div>
            )}

          </div>
        </div>

        {/* ───── 조건부 추가 인허가 7종 ───── */}
        <div>
          <SectionLabel>조건부 추가 인허가</SectionLabel>
          <p className="text-[11px] text-fg-subtler mb-3">
            위 9단계와 별도로 매장 조건에 따라 필요한 신고·허가입니다.
          </p>
          <div className="space-y-2">
            {ADDON_STEPS_LIST.map((step) => {
              if (!shouldShow(step.id)) return null;
              return (
                <StepCard
                  key={step.id}
                  step={step}
                  dim={!active(step.id)}
                  addon
                  onClick={step.detail ? () => setSelectedStepId(step.id) : undefined}
                />
              );
            })}
            {/* 모두 숨겨진 경우 */}
            {filterMode &&
              ADDON_STEPS_LIST.every((s) => !active(s.id)) && (
                <p className="text-xs text-fg-subtler text-center py-4">
                  현재 조건에 해당하는 추가 인허가가 없습니다.
                </p>
              )}
          </div>
        </div>

        {/* 출처 */}
        <p className="text-[11px] text-fg-subtler text-center leading-relaxed pb-4">
          출처: 국세청, 식약처, 공정거래위원회, 정부24, 국가법령정보센터 (조사일: 2026-05-14)
        </p>
      </main>

      {/* 단계 상세 모달 */}
      {selectedStepId && allStepsMap[selectedStepId]?.detail && (
        <StepDetailDialog
          step={allStepsMap[selectedStepId]!}
          onClose={() => setSelectedStepId(null)}
        />
      )}
    </div>
  );
}

/** 마스터 스위치 */
function MasterSwitch({
  checked,
  onToggle,
}: {
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={onToggle}
      className={[
        'relative inline-flex h-7 w-14 shrink-0 rounded-full border-2 border-transparent',
        'transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'cursor-pointer',
        checked ? 'bg-fg' : 'bg-bdr-subtle',
      ].join(' ')}
    >
      <span
        className={[
          'pointer-events-none inline-block h-6 w-6 rounded-full bg-surface shadow-sm',
          'transform transition-transform duration-200',
          checked ? 'translate-x-7' : 'translate-x-0',
        ].join(' ')}
      />
    </button>
  );
}

/** 섹션 레이블 */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="text-xs font-semibold text-fg-subtle">{children}</span>
      <div className="flex-1 h-px bg-bdr-subtlest" />
    </div>
  );
}
